import { type Timestamp, type WithRef } from '@principle-theorem/shared';
import { type IPractice } from '../../practice/practice';
import { type IImportProposalExtractor } from './import-extraction';
import {
  type IImportExistingContent,
  type IImportFromWebsiteResponse,
} from './import-request';
import { type IWebsiteCrawler } from './website-crawl';
import { type IWebsiteSnapshot, type IWebsiteSource } from './website-snapshot';

/**
 * Collaborators a spec can replace with fakes. Anything left out is built
 * inside `run()`, never in the constructor: `BaseHTTPSV2Function.export`
 * constructs the callable while `http/index.ts` loads, so a throw there (such
 * as an unconfigured Exa key) would break discovery of every function.
 */
export interface IImportFromWebsiteDependencies {
  crawler?: IWebsiteCrawler;
  extractor?: IImportProposalExtractor;
  now?: () => Timestamp;
}

/** Everything one import needs once the caller has passed the gates. */
export interface IWebsiteImportContext {
  practice: WithRef<IPractice>;
  source: IWebsiteSource;
  existingContent: IImportExistingContent;
  /**
   * The injected collaborators. A crawler or extractor left out is built only
   * when the import reaches the step that uses it, so an import that never
   * gets there resolves no Exa key and constructs no model client.
   */
  dependencies: IImportFromWebsiteDependencies;
  now: () => Timestamp;
  /**
   * When `run()` started, on the same clock as `now`. The extractor's retry
   * cut-off counts from it.
   */
  requestTime: Timestamp;
}

/** What a fresh read found on the website and chose to read. */
export interface IWebsiteImportDiscoveryCounts {
  linksFound: number;
  searchResults: number;
  pagesChosen: number;
}

/** How an import ended, plus the counts only its log line reports. */
export interface IWebsiteImportOutcome {
  response: IImportFromWebsiteResponse;
  fromSnapshot: boolean;
  failedPages: number;
  /**
   * Left out when a stored snapshot was reused: nothing was looked for then,
   * and zeros would read as a website that links to nothing.
   */
  discoveryCounts?: IWebsiteImportDiscoveryCounts;
}

export type WebsiteImportPageCounts = Pick<
  IWebsiteSnapshot,
  'pagesRequested' | 'failedPages'
>;
